// Billing: the signed-in account's subscription (GET /api/billing/summary) and Stripe's billing
// portal (POST /api/billing/portal), plus the money/date wording the signup and billing screens share.
// Contract: packages/shared/src/signup.ts. Query key: ["billing", "summary"].

import { PLAN_TIER_INFO, type Plan, type Role } from "@ml/shared";
import type { BillingSubscription, BillingSummary } from "@ml/shared/signup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "./api";

export const BILLING_SUMMARY_KEY = ["billing", "summary"] as const;

export const billingApi = {
  summary: () => api<BillingSummary>("/api/billing/summary"),
  /** Stripe's billing portal: change plan, update the card, cancel. */
  portal: () => api<{ url: string }>("/api/billing/portal", { method: "POST", body: {} }),
};

/** The account's subscription. Pass `enabled: false` for accounts that can't have one (reseller clients). */
export function useBillingSummary(enabled = true) {
  return useQuery({ queryKey: BILLING_SUMMARY_KEY, queryFn: billingApi.summary, enabled, staleTime: 60_000 });
}

/**
 * Opens Stripe's billing portal. Stays "pending" through the redirect: use
 * `loading={portal.isPending || portal.isSuccess}` so the button doesn't wake up mid-navigation.
 */
export function useBillingPortal() {
  return useMutation({
    mutationFn: billingApi.portal,
    onSuccess: ({ url }) => window.location.assign(url),
  });
}

/** A direct customer whose plan lives in Stripe: plan changes go through the billing portal. */
export function canChangePlanInPortal(summary: BillingSummary | null | undefined): boolean {
  return !!summary?.manageable && !!summary.subscription && !summary.subscription.reseller;
}

// ── Wording ──────────────────────────────────────────────────────────────────

/** 4900 → "$49", 990 → "$9.90", 29900 → "$299". */
export function formatUsd(cents: number): string {
  const whole = Math.round(cents) % 100 === 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** "Oct 25" this year, "Oct 25, 2027" otherwise. */
export function formatDay(ms: number, now = Date.now()): string {
  const sameYear = new Date(ms).getFullYear() === new Date(now).getFullYear();
  return new Intl.DateTimeFormat("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" }).format(
    new Date(ms),
  );
}

/** "October 25, 2026": for anything a customer may hold us to (when the trial ends). */
export function formatLongDate(ms: number): string {
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric" }).format(new Date(ms));
}

export function subscriptionPlanName(sub: Pick<BillingSubscription, "plan" | "reseller">): string {
  if (sub.reseller) return "Reseller Program";
  return sub.plan ? PLAN_TIER_INFO[sub.plan].name : "Custom plan";
}

/** "$49/month", or null when the API didn't send a price. */
export function subscriptionPriceText(sub: Pick<BillingSubscription, "amountCents" | "interval">): string | null {
  return sub.amountCents === null || sub.amountCents === undefined ? null : `${formatUsd(sub.amountCents)}/${sub.interval === 'year' ? 'year' : 'month'}`;
}

export type BillingTone = "neutral" | "accent" | "success" | "warn" | "danger" | "info";

export interface BillingStatusView {
  /** Short badge label: "Free trial", "Active", "Past due"… */
  label: string;
  tone: BillingTone;
  /** The full line: "Free trial — first charge on Oct 25". */
  text: string;
  /** A follow-up sentence worth saying out loud (trial: when to cancel to pay nothing). */
  hint: string | null;
}

/** How a subscription's state reads to the customer. */
export function billingStatus(sub: BillingSubscription, now = Date.now()): BillingStatusView {
  const day = (ms: number | null) => (ms ? formatDay(ms, now) : null);
  const trialEnd = day(sub.trialEndsAt);
  const periodEnd = day(sub.currentPeriodEnd);
  switch (sub.status) {
    case "trialing":
      if (sub.cancelAtPeriodEnd) {
        return {
          label: "Free trial",
          tone: "warn",
          text: trialEnd ? `Free trial — ends on ${trialEnd}, then your subscription cancels` : "Free trial — cancels when it ends",
          hint: "You won't be charged.",
        };
      }
      return {
        label: "Free trial",
        tone: "accent",
        text: trialEnd ? `Free trial — first charge on ${trialEnd}` : "Free trial",
        hint: trialEnd ? `Cancel before ${trialEnd} and you won't be charged.` : null,
      };
    case "active":
      if (sub.cancelAtPeriodEnd) {
        return { label: "Canceling", tone: "warn", text: periodEnd ? `Cancels on ${periodEnd}` : "Cancels at the end of this period", hint: null };
      }
      return { label: "Active", tone: "success", text: periodEnd ? `Active — renews on ${periodEnd}` : "Active", hint: null };
    case "past_due":
    case "unpaid":
      return {
        label: "Past due",
        tone: "danger",
        text: "Past due — your last payment didn't go through",
        hint: "Update your card to keep Magically Linked running.",
      };
    case "incomplete":
      return { label: "Incomplete", tone: "warn", text: "Payment incomplete", hint: "Finish the payment or update your card." };
    case "canceled":
    case "incomplete_expired":
      return { label: "Canceled", tone: "neutral", text: "Canceled", hint: null };
    case "paused":
      return { label: "Paused", tone: "neutral", text: "Paused", hint: null };
    default: {
      const label = sub.status ? sub.status.charAt(0).toUpperCase() + sub.status.slice(1).replace(/_/g, " ") : "Unknown";
      return { label, tone: "neutral", text: label, hint: null };
    }
  }
}

/** What to say when an account has no subscription of its own. */
export function noSubscriptionCopy(user: { parentId: string | null; plan: Plan | string; role: Role }): { title: string; text: string } {
  if (user.parentId) {
    return {
      title: "Billed by your account manager",
      text: "Your Magically Linked account comes through your account manager, and they bill you directly. Questions about your plan or price go to them.",
    };
  }
  if (user.plan === "lifetime") {
    return { title: "You own it", text: "You bought Magically Linked outright. There's no subscription and nothing to renew." };
  }
  return {
    title: "No subscription on this account",
    text: "Reseller clients are billed by their reseller, and lifetime buyers own the software. If you expected a subscription here, talk to us.",
  };
}
